import { useCallback, useEffect, useRef, useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/design-system/primitives/tooltip";
import { useCoarsePointer } from "@/platform/use-media-query.ts";
import { cn } from "@/design-system/cn.ts";

/**
 * The app's only tooltip. Nothing anywhere uses `title=` — a native tooltip
 * can't be styled, ignores the shared timing, and simply never appears on a
 * phone, which is where most of this app is used.
 *
 * Pointer-appropriate triggers:
 *   fine (mouse)   — hover, using the provider's shared enter delay so moving
 *                    along a row of controls doesn't strobe.
 *   coarse (touch) — long-press. Radix deliberately suppresses touch-hover, so
 *                    the press gesture is driven here: hold to reveal, and the
 *                    tap that would otherwise fire on release is swallowed.
 *
 * Placement: the content flips and shifts to stay on screen (see the collision
 * padding in the primitive), but flipping needs somewhere to go. For a control
 * pinned to an edge, pick a side PERPENDICULAR to that edge — `side="left"` on
 * something hard against the right edge has no room on either side of it once
 * the viewport is narrow.
 */

/** How long a touch must be held before the hint appears. */
const PRESS_MS = 450;
/** How long it lingers after the finger lifts, so it can actually be read. */
const LINGER_MS = 1600;
/** Movement past this many px is a scroll, not a press. */
const SLOP_PX = 10;

export interface HintProps {
  /** The tooltip body. Rich content is fine — this is a real element. */
  label: React.ReactNode;
  /** A dimmer second line: units, a shortcut, a caveat. */
  detail?: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  /** Must be a single element that forwards ref + props (any button works). */
  children: React.ReactElement;
  className?: string;
}

export function Hint({
  label,
  detail,
  side = "top",
  align = "center",
  sideOffset = 8,
  children,
  className,
}: HintProps) {
  const coarse = useCoarsePointer();
  const [open, setOpen] = useState(false);

  const pressTimer = useRef<number | undefined>(undefined);
  const lingerTimer = useRef<number | undefined>(undefined);
  const origin = useRef<{ x: number; y: number } | null>(null);
  /** Set once a long-press fires, so the trailing click doesn't also activate. */
  const swallowClick = useRef(false);

  const clearTimers = useCallback(() => {
    window.clearTimeout(pressTimer.current);
    window.clearTimeout(lingerTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  // Any touch elsewhere on the page dismisses a lingering hint immediately.
  useEffect(() => {
    if (!open || !coarse) return;
    const dismiss = () => setOpen(false);
    window.addEventListener("touchstart", dismiss, { passive: true, capture: true });
    window.addEventListener("scroll", dismiss, { passive: true, capture: true });
    return () => {
      window.removeEventListener("touchstart", dismiss, { capture: true });
      window.removeEventListener("scroll", dismiss, { capture: true });
    };
  }, [open, coarse]);

  const touchProps = coarse
    ? {
        onPointerDown: (e: React.PointerEvent) => {
          if (e.pointerType !== "touch") return;
          clearTimers();
          origin.current = { x: e.clientX, y: e.clientY };
          pressTimer.current = window.setTimeout(() => {
            swallowClick.current = true;
            setOpen(true);
          }, PRESS_MS);
        },
        onPointerMove: (e: React.PointerEvent) => {
          const start = origin.current;
          if (!start) return;
          if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > SLOP_PX) {
            clearTimers();
            origin.current = null;
          }
        },
        onPointerUp: () => {
          window.clearTimeout(pressTimer.current);
          origin.current = null;
          if (!open) return;
          lingerTimer.current = window.setTimeout(() => setOpen(false), LINGER_MS);
        },
        onPointerCancel: () => {
          clearTimers();
          origin.current = null;
          setOpen(false);
        },
        // A long press would otherwise raise the OS selection/callout menu.
        onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
        onClickCapture: (e: React.MouseEvent) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.preventDefault();
          e.stopPropagation();
        },
      }
    : {};

  return (
    <Tooltip
      open={open}
      // On a coarse pointer the press gesture is the only thing allowed to open
      // this; Radix's own hover intent is ignored.
      onOpenChange={(next) => {
        if (!coarse) setOpen(next);
      }}
    >
      <TooltipTrigger asChild className={coarse ? "press-target" : undefined} {...touchProps}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn("max-w-[min(14rem,calc(100vw-1.5rem))]", className)}
      >
        <p className="text-[12px] leading-snug font-medium">{label}</p>
        {detail && <p className="mt-0.5 text-[11px] leading-snug text-chalk-mute">{detail}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
